import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from 'src/shared/components/ui/use-toast';
import { useOrg } from 'src/shared/contexts/OrgContext';
import mixpanel from 'src/shared/lib/mixpanel';
import { genQueryKey } from 'src/shared/lib/query.utils';
import { ClassificationModel } from 'src/shared/types/classification-model.types';
import { IntegrationId } from 'src/shared/types/integration.types';

type NewClassificationModelProps = {
  modelId: string;
  name: string;
  description?: string;
  isMultilabel: boolean;
  integrations?: IntegrationId[];
  labels?: {
    name: string;
    description?: string;
    parentLabelId?: string;
  }[];
};

type UpdateClassificationModelProps = {
  modelId: string;
  name?: string;
  description?: string;
  integrations?: IntegrationId[];
  status?: 'ACTIVE' | 'INACTIVE';
  includeAttachments?: boolean;
};

export function useMutateClassificationModel() {
  const queryClient = useQueryClient();
  const { orgFetch, currentOrg, role } = useOrg();
  const { errorToast } = useToast();

  const queryKey = ['next-gen', 'models', 'classification', genQueryKey(currentOrg.id)];
  const singleModelQueryKey = (modelId: string) => [
    'next-gen',
    'classification-models',
    modelId,
    genQueryKey(currentOrg.id),
  ];

  const newClassificationModel = useMutation({
    mutationFn: async ({
      modelId,
      name,
      description,
      isMultilabel,
      integrations,
      labels,
    }: NewClassificationModelProps) => {
      if (role === 'SALES') {
        throw new Error('Sales users cannot create models');
      }
      const res = await orgFetch<ClassificationModel>(`/api/v2/next-gen-models/classification`, {
        method: 'POST',
        body: JSON.stringify({
          name: name || modelId,
          description: description || '',
          isMultilabel,
          labels: labels || [],
          integrations: integrations || [],
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error('Unable to create classification model');
      }
      return data;
    },
    onSuccess: (data: ClassificationModel) => {
      // Invalidate the list query to refetch
      queryClient.invalidateQueries({ queryKey });
      // Set the single model in cache
      queryClient.setQueryData(singleModelQueryKey(data.id), data);
      mixpanel.track('Next-Gen Classification Model Created');
    },
  });

  const updateClassificationModel = useMutation({
    mutationFn: async ({ modelId, ...data }: UpdateClassificationModelProps) => {
      if (role === 'SALES') {
        throw new Error('Sales users cannot update models');
      }
      const res = await orgFetch<ClassificationModel>(`/api/v2/next-gen-models/classification/${modelId}`, {
        method: 'PATCH',
        body: JSON.stringify(data),
      });
      const responseData = await res.json();
      if (!res.ok) {
        throw new Error('Classification model update failed');
      }
      return responseData;
    },
    onMutate: async ({ modelId, ...data }: UpdateClassificationModelProps) => {
      await queryClient.cancelQueries({ queryKey: singleModelQueryKey(modelId) });
      const previousData = queryClient.getQueryData<ClassificationModel>(singleModelQueryKey(modelId));

      // Optimistically update the cache
      if (previousData) {
        queryClient.setQueryData<ClassificationModel>(singleModelQueryKey(modelId), {
          ...previousData,
          ...data,
        });
      }

      return { previousData };
    },
    onSuccess: (data: ClassificationModel) => {
      // Update both single model and list caches
      queryClient.setQueryData(singleModelQueryKey(data.id), data);
      queryClient.invalidateQueries({ queryKey });
      mixpanel.track('Next-Gen Classification Model Updated');
    },
    onError: (_, __, context) => {
      errorToast('Failed to update classification model');
      // Revert optimistic update
      if (context?.previousData) {
        queryClient.setQueryData(
          singleModelQueryKey((context.previousData as ClassificationModel).id),
          context.previousData
        );
      }
    },
  });

  const deleteClassificationModel = useMutation({
    mutationFn: async (modelId: string) => {
      if (role === 'SALES') {
        throw new Error('Sales users cannot delete models');
      }
      const res = await orgFetch<{ message: string }>(`/api/v2/next-gen-models/classification/${modelId}`, {
        method: 'DELETE',
      });
      if (!res.ok) {
        throw new Error('Unable to delete classification model');
      }
      return modelId;
    },
    onSuccess: (modelId: string) => {
      // Remove from single model cache
      queryClient.removeQueries({ queryKey: singleModelQueryKey(modelId) });
      // Invalidate list to refetch
      queryClient.invalidateQueries({ queryKey });
      mixpanel.track('Next-Gen Classification Model Deleted');
    },
    onError: () => {
      errorToast('Failed to delete classification model');
    },
  });

  return {
    newClassificationModel,
    updateClassificationModel,
    deleteClassificationModel,
  };
}
